<template>
  <iframe
    :id="iframeId"
    ref="iframeRef"
    :src="formUrl"
    frameborder="0"
    hspace="0"
    allowtransparency="true"
    style="width: 100%; overflow: hidden; margin: 0"
  />
</template>

<script setup lang="ts">
import { v } from 'h3-valibot';
import { useIframeResizer } from '~/composables/useIframeResizer';
import { jsonParseBaseSchema } from '~/utils/validation/jsonParseBaseSchema';

const { loggedIn, user } = useOidcAuth();

interface Props {
  formUrl: string;
  id: string | undefined;
}

const props = defineProps<Props>();

const emits = defineEmits<{
  success: [];
}>();

const logger = useLogger();
const iframeRef = ref<HTMLIFrameElement>();

const iframeId = computed(() => `pardot-form-iframe-${props.id}`);
useIframeResizer(iframeRef, iframeId.value);

const successMessageModel = jsonParseBaseSchema(
  v.object({
    isDownload: v.boolean()
  })
);

const formUrl = ref(
  enrichUrlWithLoginData(
    new URL(props.formUrl),
    loggedIn.value,
    user.value
  ).toString()
);

watch(
  user,
  () => {
    if (import.meta.client) {
      formUrl.value = enrichUrlWithLoginData(
        new URL(props.formUrl),
        loggedIn.value,
        user.value
      ).toString();
      document
        .getElementById(iframeId.value)
        ?.setAttribute('src', formUrl.value);
    }
  },
  { immediate: true }
);

function handlePostMessage(event: MessageEvent) {
  if (!props.formUrl) {
    return;
  }

  let origin;

  try {
    origin = new URL(props.formUrl).origin;
  } catch (e) {
    logger.error('failed to retrieve origin from downloadFormUrl', e);

    return;
  }

  if (
    event.origin !== origin ||
    (typeof event.data === 'string' && event.data.includes('iFrameSizer'))
  ) {
    return;
  }

  const data = v.safeParse(successMessageModel, event.data);

  if (data.success && data.output.isDownload) {
    emits('success');
  } else if (!data.success) {
    logger.error('failed to parse post message from pardot form', data.issues);
  }
}

onMounted(() => {
  window.addEventListener('message', handlePostMessage, false);
});

onUnmounted(() => {
  window.removeEventListener('message', handlePostMessage);
});
</script>
